import { useCallback, useMemo, useState } from 'react';
import type { ValidationRepairPanelTab } from '../../../shared/stores/usePcfValidationStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import {
  buildUndoQuickFixAction,
  runValidationAfterRepair,
} from '../lib/validationDetailRepairActions';
import type { ValidationIssueFamily } from '../lib/validationIssueFamilies';
import { useValidationQuickFixFlow } from './useValidationQuickFixFlow';

interface FamilyReconnectAllAction {
  label: string;
  validationId: number;
}

interface UseValidationIssueRepairActionsOptions {
  family: ValidationIssueFamily | null;
  familyReconnectAllAction: FamilyReconnectAllAction | null;
  primaryActionOpensRepairWorkspace: boolean;
  onOpenRepairDetail?: (
    family: ValidationIssueFamily,
    options?: { activeTab?: ValidationRepairPanelTab; surgeryMode?: boolean }
  ) => void;
  onReconnectAll?: (validationId: number) => Promise<void> | void;
  onRunValidationScoped?: (validationIds: number[]) => Promise<void> | void;
}

export interface ValidationIssueRepairAction {
  label: string;
  tone: 'primary' | 'secondary';
  onClick: () => void | Promise<void>;
}

export function useValidationIssueRepairActions({
  family,
  familyReconnectAllAction,
  primaryActionOpensRepairWorkspace,
  onOpenRepairDetail,
  onReconnectAll,
  onRunValidationScoped,
}: UseValidationIssueRepairActionsOptions) {
  const snackbar = useSnackbarStore();
  const quickFixTransactions = usePcfValidationStore((state) => state.quickFixTransactions);
  const { undoQuickFixTransaction } = useValidationQuickFixFlow();
  const [undoingQuickFix, setUndoingQuickFix] = useState(false);
  const [undoProgress, setUndoProgress] = useState<OperationProgressModel | null>(null);

  const latestUndoableTransaction = useMemo(
    () =>
      quickFixTransactions.find(
        (transaction) =>
          transaction.familyId === family?.id &&
          transaction.status === 'applied' &&
          transaction.undoAvailable
      ) ?? null,
    [family?.id, quickFixTransactions]
  );

  const runAffectedValidations = useCallback(
    (validationIds: number[]) =>
      runValidationAfterRepair(validationIds, { onRunValidationScoped }),
    [onRunValidationScoped]
  );

  const repairWorkspaceActions = useMemo<ValidationIssueRepairAction[]>(() => {
    if (!family) {
      return [];
    }

    const actions: ValidationIssueRepairAction[] = [];
    if (onOpenRepairDetail && !primaryActionOpensRepairWorkspace) {
      actions.push({
        label: 'Open repair workspace',
        tone: 'primary',
        onClick: () =>
          onOpenRepairDetail(family, {
            activeTab: 'recommended',
            surgeryMode: false,
          }),
      });
    }

    if (familyReconnectAllAction) {
      actions.push({
        label: familyReconnectAllAction.label,
        tone: 'secondary',
        onClick: () => {
          if (!onReconnectAll) {
            snackbar.showError('Reconnect all is not available for this issue.');
            return;
          }

          void onReconnectAll(familyReconnectAllAction.validationId);
        },
      });
    }

    if (latestUndoableTransaction) {
      actions.push(
        buildUndoQuickFixAction({
          transaction: latestUndoableTransaction,
          undoQuickFixTransaction,
          showSuccess: snackbar.showSuccess,
          showError: snackbar.showError,
          setUndoingQuickFix,
          setUndoProgress,
          onAfterRepair: runAffectedValidations,
        })
      );
    }

    return actions;
  }, [
    family,
    familyReconnectAllAction,
    latestUndoableTransaction,
    onOpenRepairDetail,
    onReconnectAll,
    primaryActionOpensRepairWorkspace,
    runAffectedValidations,
    snackbar,
    undoQuickFixTransaction,
  ]);

  return {
    repairWorkspaceActions,
    runAffectedValidations,
    undoingQuickFix,
    undoProgress,
  };
}
